import { Card, CardBody, CardHeader, Col, Row } from 'react-bootstrap';
import { Form, FormSpy } from 'react-final-form';
import { getLanguage, t } from 'react-switch-lang';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link, { ExternalLink } from '../../Link';
import SectionTitle from '../../SectionTitle';
import styles from '../../../styles/AccountDetails.module.scss';
import { events, logAmpEvent } from '../../../utils/Amplitude';
import Field from '../../InputFields';
import { formatAmountEn, formatAmountFr, formatPaymentAmount, getLanguageString, getText, invalidFormAlert, mailcheckValidate, parseAmountStr, normalizeFormValues } from '../../../utils/Helpers';
import Button from '../../Button';
import { FIELD_VALIDATION_REGEX, FormatAmount, getDefaultMask, validationHelper } from '../../../utils/Validations';
import { DEFAULT_ACCOUNT_NAME_MAX_LENGTH, PAYMENT_AMOUNT_LIMITS } from '../../../utils/Constants';
import { DISPATCHERS, usePaymentProcessContext } from '../../../context/PaymentProcessContext';
import RenderMarkdown from '../../RenderMarkdown';
import { setScreenReaderAlert } from '../../../utils/Accessibility';
import ConfirmationModal from '../../ConfirmationModal';
import { RecipientLogo } from '../PaymentInformation';
import DebugInfo from '../../DebugInfo';

const showToolTip = (labelObj, defaultLabelKey) => {
  if (labelObj?.Key) return labelObj.Key === defaultLabelKey;
  return false;
};

function PaymentAmountMaxAmountWarning() {
  useEffect(() => {
    setScreenReaderAlert(t('AccountDetails_MaxAmount_Warning')
      .replace('xMaxLimit', FormatAmount(PAYMENT_AMOUNT_LIMITS.MAX)));
  }, []);
  return (
    <div className={styles.maxAmountWarningContainer}>
      {
        t('AccountDetails_MaxAmount_Warning')
          .replace('xMaxLimit', FormatAmount(PAYMENT_AMOUNT_LIMITS.MAX))
      }
      {' '}
      <ExternalLink
        href={t('Link_PaySimply_Help_TransactionLimits')}
        text={t('AccountDetails_MaxAmount_HelpCentre_LinkText')}
        ampEvent={events.USER_CLICKED_MAX_AMOUNT_LIMIT_HELP_LINK}
        aria-label={t('AccountDetails_MaxAmount_HelpCentre_LinkDescription')}
      />
    </div>
  );
}

function IntuitBanner() {
  const {
    paymentProcess: {
      siteMode,
    },
  } = usePaymentProcessContext();
  const isIntuit = siteMode === 'Intuit';

  if (!isIntuit) return null;

  return (
    <CardHeader id={styles.IntuitBanner}>
      <p>{t('AccountDetails_IntuitBanner_Title')}</p>
      <p>{t('AccountDetails_IntuitBanner_Body')}</p>
    </CardHeader>
  );
}

export default function AccountDetails({ autofill = null, entryType = null }) {
  const {
    dispatchPaymentProcess,
    paymentProcess: {
      landingPageData,
      accountDetails,
      partner,
      algoliaObject,
      isInvoice,
    },
  } = usePaymentProcessContext();
  const router = useRouter();
  const config = partner?.PSWebConfig;
  const fields = config?.Fields || [];
  const accountNameLabel = config?.AccountNameLabel;
  const accountNameMaxLength = config?.AccountNameMaxLength;
  const clientRefField = fields?.find((field) => field.DBField === 'ClientRef');
  const lang = getLanguage();
  const bannerText = lang === 'en' ? config?.BannerText?.En : config?.BannerText?.Fr;
  const [invalidEmailSuggestion, setInvalidEmailSuggestion] = useState(null);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const recipientName = getText(partner?.DisplayName);
  const emailAddressValidations = {
    MaxLength: 50,
    Regex: { regex: FIELD_VALIDATION_REGEX.VALID_EMAIL, customErrorMessage: t('Validation_Type_Email') },
  };
  const accountNameValidations = {
    MaxLength: accountNameMaxLength || DEFAULT_ACCOUNT_NAME_MAX_LENGTH,
    Regex: { regex: FIELD_VALIDATION_REGEX.VALID_CHAR, customErrorMessage: null },
    Type: 'AccountholderName',
  };

  const validate = (values) => {
    normalizeFormValues(values);
    const errors = {};

    errors.Email =
    validationHelper(emailAddressValidations, values.Email)?.errorMessage || undefined;

    if (!errors.Email && !showEmailConfirm) {
      const email = values.Email.toLowerCase().trim();
      const suggestedEmail = mailcheckValidate(email);
      setInvalidEmailSuggestion(suggestedEmail);
      if (suggestedEmail) {
        errors.Email = t('AccountDetails_MailCheck_EmailError');
      }
    } else {
      setInvalidEmailSuggestion(null);
    }

    errors.FullName =
    validationHelper(accountNameValidations, values.FullName)?.errorMessage || undefined;

    fields?.forEach((field) => {
      if (field.Type === 'Hidden') {
        // Skip validation for hidden fields
        errors[field.DBField] = undefined;
      } else {
        const validationResult = validationHelper(
          field.Validation,
          values[field.DBField],
          field.Mask
        );
        // If field name is not available, using the Label.
        const fieldName = getText(field.Name) || getText(field.Label);
        errors[field.DBField] = validationResult?.errorMessage?.replace('xField', fieldName) || undefined;
      }
    });

    const paymentAmountValidations = {
      MaxLength: 11,
      Amount: {
        MinPaymentAmount: PAYMENT_AMOUNT_LIMITS.MIN,
        MaxPaymentAmount: PAYMENT_AMOUNT_LIMITS.MAX,
      },
    };
    if (values.PaymentAmount) {
      const parsedAmount = parseAmountStr(values.PaymentAmount);
      errors.PaymentAmount =
        validationHelper(paymentAmountValidations, parsedAmount)?.errorMessage || undefined;
    } else {
      errors.PaymentAmount = t('Validation_Required');
    }

    return errors;
  };

  const onSubmit = async (values) => {
    normalizeFormValues(values);
    // eslint-disable-next-line no-param-reassign
    values.Email = values.Email.toLowerCase();

    const parsedAmount = parseAmountStr(values.PaymentAmount);
    // eslint-disable-next-line no-param-reassign
    values.PaymentAmount = parsedAmount;
    const accountDetailsValues = { ...values };
    // add hidden fields to the accountDetailsValues
    fields.forEach((field) => {
      if (field.Type !== 'Hidden') return;
      // if accountDetails already has a value for the hidden field,
      // it's likely passed in as a query param for Smart Payment Links, so repect that value
      // otherwise set the value as the default value
      const fieldValue = accountDetails?.[field.DBField] || field.Value;
      if (!fieldValue) return;
      accountDetailsValues[field.DBField] = fieldValue;
    });
    dispatchPaymentProcess(DISPATCHERS.ENTER_ACCOUNT_DETAILS(accountDetailsValues));
    router.push(`${window.location.pathname}#payment-option`, undefined, { shallow: true });
    return true;
  };

  const handleGoBack = () => {
    const prevPath = router.query.landingPageKey ? `/lp/${router.query.landingPageKey}` : '/partner-search';
    router.push(prevPath);
  };

  const landingPageName =
    getLanguageString(landingPageData?.LandingPageNameEn, landingPageData?.LandingPageNameFr) ||
    getLanguageString(algoliaObject?.LandingPageNameEn, algoliaObject?.LandingPageNameFr);

  useEffect(() => {
    if (!partner) return;

    const ampEventProps = {
      PartnerSeq: partner.PartnerSeq,
      'Recipient Name': partner.DisplayName.en,
    };
    if (entryType) ampEventProps['Entry Type'] = entryType;
    logAmpEvent(events.USER_VIEWED_ACCOUNT_DETAILS_PAGE, ampEventProps);
  }, [partner]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Card className={styles.paymentRecipientCardContainer}>
        <CardBody className={styles.paymentRecipientCard}>
          <h1>{t('PaymentInfo_RecipientTitle')}</h1>
          <RecipientLogo landingPageData={landingPageData} partner={partner} />
          <p>{landingPageName}</p>
          <p className={styles.recipientName}>
            {recipientName}
          </p>
        </CardBody>
      </Card>
      <Form
        // force form to re-render when partner changes
        // otherwise filled out inputs are not cleared when partner changes
        // such as when using the Revenu Québec link in the footer to change the partner
        key={partner?.PartnerSeq}
        onSubmit={onSubmit}
        validate={(values) => validate(values)}
        render={({
          handleSubmit,
          invalid,
          errors,
          form,
          values,
        }) => (
          <form
            onSubmit={(event) => {
              invalidFormAlert(invalid, errors);
              handleSubmit(event);
            }}
          >
            <FormSpy
              subscription={{ active: true, touched: true }}
              onChange={({ touched, active }) => {
                if (touched.PaymentAmount && active !== 'PaymentAmount') {
                  if (!values.PaymentAmount) return;
                  const parsedAmount = parseAmountStr(values.PaymentAmount);
                  form.change('PaymentAmount', formatPaymentAmount(parsedAmount));
                }
              }}
            />
            <Card>
              <IntuitBanner />
              <CardBody>
                <Row>
                  <Col md={12} className="d-flex flex-column justify-content-md-center">
                    <SectionTitle
                      title={t('PaymentProcess_AccountDetails_Title')}
                      subtitle={t('PaymentProcess_AccountDetails_Subtitle')}
                    />
                  </Col>
                </Row>
                {bannerText && (
                <Row className="justify-content-center">
                  <Col md={9} sm={12} className={styles.bannerTextContainer}>
                    <RenderMarkdown markdown={bannerText} explicitPTag />
                  </Col>
                </Row>
                )}
                <Row className="justify-content-center">
                  <Col md={9} sm={12}>
                    <div className={styles.fieldContainer}>
                      <Field
                        label={t('PaymentProcess_AccountDetails_EmailLabel')}
                        id="Email"
                        name="Email"
                        disabled={isInvoice}
                        placeholder={t('PaymentProcess_AccountDetails_EmailLabel')}
                        formatText={t('PaymentProcess_AccountDetails_EmailHelpText')}
                        initialValue={accountDetails?.Email}
                        mask={null}
                        change={form.change}
                        invalidEmailSuggestion={invalidEmailSuggestion}
                        setInvalidEmailSuggestion={setInvalidEmailSuggestion}
                        setShowEmailConfirm={setShowEmailConfirm}
                        showEmailConfirm={showEmailConfirm}
                        type="Email"
                      />
                    </div>
                    <div className={styles.fieldContainer}>
                      <Field
                        label={getText(accountNameLabel) || t('biller_accountHolderNameLabel')}
                        showToolTip={showToolTip(accountNameLabel, 'PayerNameField_Name')}
                        toolTipText={t('AccountDetails_AccountholderNameSynonyms')}
                        id="FullName"
                        name="FullName"
                        disabled={isInvoice}
                        autoComplete="on"
                        placeholder={getText(accountNameLabel)}
                        initialValue={accountDetails?.FullName}
                        mask={null}
                      />
                    </div>
                    <div className={styles.fieldContainer}>
                      <Field
                        label={getText(clientRefField?.Label) || t('AccountNumField_Label')}
                        showToolTip={showToolTip(clientRefField?.Label, 'AccountNumField_Label')}
                        toolTipText={t('AccountDetails_AccountNumSynonyms')}
                        formatText={getText(clientRefField?.HelpText)}
                        id="ClientRef"
                        name="ClientRef"
                        disabled={isInvoice}
                        placeholder={getText(clientRefField?.Placeholder)}
                        initialValue={accountDetails?.ClientRef || clientRefField?.Value}
                        mask={getDefaultMask(clientRefField?.Mask, clientRefField?.Validation)}
                        helpImage={clientRefField?.HelpImage}
                        helpImageAlt={
                        t('AccountDetails_Help_ImageDescription')
                          .replace('xField', getText(clientRefField?.Label))
                          .replace('xRecipient', recipientName)
                      }
                      />
                    </div>

                    {fields.map((field) => {
                      if (field.DBField === 'ClientRef') return null;
                      return (
                        <div className={styles.fieldContainer} key={field.DBField}>
                          <Field
                            label={getText(field.Label) || t(`${field.DBField}_DefaultLabel`)}
                            formatText={getText(field.HelpText)}
                            id={field.DBField}
                            name={field.DBField}
                            disabled={isInvoice}
                            placeholder={getText(field.Placeholder)}
                            initialValue={accountDetails?.[field.DBField] || field.Value}
                            type={field.Type}
                            mask={getDefaultMask(field.Mask, field.Validation)}
                            helpImage={field.HelpImage}
                            setValue={form.change}
                          />
                        </div>
                      );
                    })}
                    <div className={styles.fieldContainer}>
                      <Field
                        label={t('PaymentAmountField_Name')}
                        prefixItem={lang === 'en' && '$'}
                        suffixItem={lang === 'fr' && '$'}
                        format={lang === 'fr' ? formatAmountFr : formatAmountEn}
                        id="PaymentAmount"
                        name="PaymentAmount"
                        disabled={isInvoice}
                        inputMode="decimal"
                        placeholder={t('PaymentAmountField_Name')}
                        initialValue={accountDetails?.PaymentAmount}
                        autoComplete="on"
                        errorAboveInput={
                          Number(values.PaymentAmount) > PAYMENT_AMOUNT_LIMITS.MAX && (
                            <PaymentAmountMaxAmountWarning />
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <p className={styles.footerPrivacyNotice}>{t('PaymentProcess_AccountDetails_PrivacyNotice')}</p>
                  <p className={styles.footerTermsAndCondition}>
                    {t('PaymentProcess_AccountDetails_TermsText')}
                    {' '}
                    <Link
                      href="/terms"
                      ampEvent={events.USER_CLICKED_ACCOUNT_DETAILS_TERMS}
                    >
                      {t('PaymentProcess_AccountDetails_TermsLinkText')}
                    </Link>
                  </p>
                </Row>
              </CardBody>
            </Card>
            <ConfirmationModal
              showModal={showConfirmationModal}
              onCancel={() => setShowConfirmationModal(false)}
              onConfirm={handleGoBack}
              ampEventPropsPageName="Payment Details"
              stepName={t('ProgressBarStep2')}
              returnFocusTo="backButton"
            />
            <div className={styles.buttonContainer}>
              <Button
                text={t('PaymentProcess_ButtonBack')}
                ampEvent={events.USER_CLICKED_BACK_BUTTON}
                ampEventProps={{ Step: 'Account Details' }}
                onClick={() => setShowConfirmationModal(true)}
                id="backButton"
              />
              <Button
                text={t('PaymentProcess_ButtonContinue')}
                ampEvent={events.USER_CLICKED_CONTINUE_BUTTON}
                ampEventProps={{ Step: 'Account Details' }}
                type="submit"
              />
            </div>
            {autofill && !isInvoice && (
              <DebugInfo>
                <Button
                  debug
                  text="AUTOFILL DETAILS"
                  onClick={() => Object.entries(autofill).forEach(([k, v]) => form.change(k, v))}
                />
              </DebugInfo>
            )}
          </form>
        )}
      />
    </>
  );
}
